import { Project, DuctElement, Element } from '../types';
import { MEPSystemLogic, FlowClassification } from './MEPSystemLogic';

export interface MEPSystem {
    id: string;
    name: string;
    classification: FlowClassification;
    logic: MEPSystemLogic;
    elementIds: Set<string>; // Physical elements belonging to this system
    rootId?: string; // Mechanical Equipment ID serving as source
}

export class MEPManager {
    private systems: Map<string, MEPSystem> = new Map();

    constructor(private project: Project) { }

    /**
     * Scan the project for connected MEP networks and build logical systems.
     * This is a heavy operation, usually run on button press or major change.
     */
    public autoDetectSystems(): void {
        this.systems.clear();

        // 1. Identify all MEP Consumers (Terminals) and Producers (Equipment)
        // 2. Flood fill from Equipment to find connected networks

        const visited = new Set<string>();

        // Ideally we start from Mechanical Equipment (Sources)
        const sources = Array.from(this.project.elements.values()).filter(e =>
            e.category === 'Mechanical Equipment'
        );

        let sysCount = 1;

        sources.forEach(source => {
            if (visited.has(source.id)) return;

            // Start a new system
            const systemId = `SYS-${sysCount++}`;
            const classification = this.determineClassification(source);

            const system: MEPSystem = {
                id: systemId,
                name: `${classification} ${sysCount}`,
                classification: classification,
                logic: new MEPSystemLogic(classification),
                elementIds: new Set(),
                rootId: source.id
            };

            // Flood fill
            this.buildSystemGraph(source, system, visited);

            this.systems.set(systemId, system);

            // Calculate initial state
            if (system.rootId) {
                // Must add the root node first?
                // logic.addNode... done in buildSystemGraph
                system.logic.calculateFlows(system.rootId);
            }
        });
    }

    private determineClassification(_source: Element): FlowClassification {
        // Simplified: Check parameters or connector types
        // For now, default to Supply Air
        return 'SupplyAir';
    }

    private buildSystemGraph(current: Element, system: MEPSystem, visited: Set<string>) {
        if (visited.has(current.id)) return;
        visited.add(current.id);
        system.elementIds.add(current.id);

        // Add node to logic
        // Determine type: Equipment=Source, Terminal=Terminal, Fitting/Duct=Junction
        let type: 'Source' | 'Terminal' | 'Junction' = 'Junction';
        let flow = 0;

        if (current.category === 'Mechanical Equipment') type = 'Source';
        else if (current.category === 'Air Terminals') {
            type = 'Terminal';
            flow = current.parameters['Flow'] || 0;
        }

        system.logic.addNode(current.id, type, flow);

        // Find neighbors via connectors
        // Using GeometryData or explicit 'connectors' if available. 
        // Falling back to existing ConstraintSolver logic for 'JOIN' constraints which represents connectivity.

        const connectedIds = current.constraints
            .filter(c => c.type === 'JOIN' || c.type === 'HOST_FACE') // Simplified connectivity check
            .map(c => c.targetId);

        connectedIds.forEach(neighborId => {
            const neighbor = this.project.elements.get(neighborId);
            if (neighbor && this.isMEPElement(neighbor)) {

                // Add Link if not exists
                // We need a unique link ID for the connection. Using sorted IDs.
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const _linkId = [current.id, neighborId].sort().join('-');

                // Length and Size need to be extracted from the Segment (Duct/Pipe)
                // If 'neighbor' is a Duct, use its props. If 'current' is Duct, use its props.
                // Connection itself implies a segment exists between nodes? 
                // Or are Elements Nodes AND Links? 
                // In BIM: Fittings = Nodes, Ducts = Edges (Links)

                // Graph Model:
                // Node = Fitting / Terminal / Equipment
                // Link = Duct / Pipe Curve

                // If CURRENT is a Duct, it IS the link between two fittings.
                // This implies a graph transformation: 
                // Physical: Fitting -> Duct -> Fitting
                // Logical: Node -> Link -> Node

                // Let's stick to this:
                // If neighbor is unvisited, recurse
                this.buildSystemGraph(neighbor, system, visited);

                // Handling the "Duct is a Link" logic:
                // If we are traversing FROM a fitting TO a duct...
                // We'll treat Duct as a Link in logic, but also as a "Passthrough Node" for connectivity?
                // Simpler: 
                // Just Graph it: Node for every Element. Link for every connection.
                // Duct = Node (Junction) with Length/Resistance.
                // Connection = 0-length Link.
                // This is less efficient but easier to map 1:1.

                // Refined approach:
                // Duct IS a Link. Fittings ARE Nodes.
                // If current is Duct:
                //   It connects Node A and Node B.
                //   We addLink(duct.id, nodeA, nodeB)
                // If current is Fitting:
                //   It is a Node.

                if (current.category === 'Ducts') {
                    // Duct should have been added as Link when processing the Fitting?
                    // Or we explicitly handle Ducts.
                    this.addDuctLink(current as DuctElement, system);
                }
            }
        });
    }

    private addDuctLink(duct: DuctElement, system: MEPSystem) {
        // A duct connects two "Nodes" (Fittings/Terminals/Equipment).
        // The graph builder visited the Duct via a Node.
        // We need to find the TWO nodes this duct connects.
        // This implies we need to look at the duct's connected elements.

        const connectedIds = duct.constraints
            .filter(c => c.type === 'JOIN' || c.type === 'HOST_FACE')
            .map(c => c.targetId);

        if (connectedIds.length >= 2) {
            const nodeA = connectedIds[0];
            const nodeB = connectedIds[1];

            // Check if both nodes are part of the system (or will be)
            // We can just add the link. If nodes are missing, Logic might handle or ignore.
            // Ideally we enable 'lazy' node adding?
            // But buildSystemGraph adds nodes as it visits.

            // Calculate Hydraulic Diameter
            // D_h = 4A / P
            // Rect: 4*(W*H) / (2*(W+H)) = 2WH / (W+H)
            let size = 0.3; // default
            if (duct.geometryData.width && duct.geometryData.height) {
                const w = duct.geometryData.width;
                const h = duct.geometryData.height;
                size = (2 * w * h) / (w + h);
            } else if ((duct as any).diameter) {
                size = (duct as any).diameter;
            }

            // Real segment length from the duct's own polyline geometry.
            const length = this.ductPolylineLength((duct.geometryData as any).path ?? []);

            system.logic.addLink(duct.id, nodeA, nodeB, size, length);
        }
    }

    /** Summed 3D length of the duct's path polyline. */
    private ductPolylineLength(path: [number, number, number][]): number {
        let len = 0;
        for (let i = 1; i < path.length; i++) {
            const [x1, y1, z1] = path[i - 1];
            const [x2, y2, z2] = path[i];
            len += Math.hypot(x2 - x1, y2 - y1, z2 - z1);
        }
        return len;
    }

    private isMEPElement(el: Element): boolean {
        return ['Ducts', 'Pipes', 'Pipe Fittings', 'Duct Fittings', 'Air Terminals', 'Mechanical Equipment'].includes(el.category);
    }

    getSystem(id: string) {
        return this.systems.get(id);
    }

    getAllSystems(): Map<string, MEPSystem> {
        return this.systems;
    }
}
